/**
 * Saved Work (Monitor) ticket docTypes that are eligible for scheduled
 * re-screening — the ongoing entity/product monitors.
 *
 * This MUST stay in sync with the automations service constant
 * `RE_SCREENABLE_DOC_TYPES` in
 * `automations/src/processors/TicketBatchProcessor.ts`, which is what the
 * nightly batch actually filters on. Every other docType (Bulk Screening,
 * Single Search, bills of lading, questionnaires, etc.) is a one-off workflow
 * that never re-screens — so in the UI its screening frequency is shown as
 * "N/A" and the frequency settings modal is not openable, to avoid implying a
 * schedule that will never run. Tariff tickets are re-priced rather than
 * re-screened; see isMonitoredTariffTicket.
 */
export const RE_SCREENABLE_DOC_TYPES = [
  'Person / Company',
  'Import Product Screen',
  'Export Product Screen',
];

export const isReScreenableDocType = (docType?: string | null): boolean =>
  !!docType && RE_SCREENABLE_DOC_TYPES.includes(docType);

/**
 * Tariff tickets on a recurring frequency are re-priced by the biteservices
 * product service (cron/due-tariff-ticket-checks.ts), which flags and notifies
 * on a change. US only: the duty engine does not price Canada or Mexico, and a
 * ticket saved before tariffJurisdiction existed is a US ticket.
 */
export const isMonitoredTariffTicket = (
  docType?: string | null,
  tariffJurisdiction?: string | null,
): boolean =>
  docType === 'Tariff' && (!tariffJurisdiction || tariffJurisdiction === 'US');

/** Whether a ticket's frequency setting does anything. */
export const canScheduleTicket = (
  docType?: string | null,
  tariffJurisdiction?: string | null,
): boolean =>
  isReScreenableDocType(docType) ||
  isMonitoredTariffTicket(docType, tariffJurisdiction);
